/**
 * Dogfood Spotlight on dashboard.spotlight.help (card 049).
 *
 * The dashboard ships the Pinpoint help widget by way of the same
 * SDK we ship to customers — both as a daily-use canary for SDK
 * regressions and as a direct line from dashboard users into the
 * Spotlight platform tenant's Pinpoint inbox.
 *
 * Wiring shape:
 *
 *   1. `dashboard/index.html` loads the SDK with `<script defer
 *      src="https://sdk.spotlight.help/latest/spotlight.min.js">`.
 *      `defer` means the script runs after HTML parsing but before
 *      `DOMContentLoaded` — which can land before or after React's
 *      first render.  This component polls `window.Spotlight` to
 *      paper over the race.
 *
 *   2. `VITE_SPOTLIGHT_PK_KEY` is the publishable (`pk_live_*`)
 *      key for the Spotlight platform tenant, baked into the
 *      dashboard bundle at build time.  Origin lockdown via the
 *      tenant's Sites allow-list is what prevents this key from
 *      being abused off-domain — see docs/runbooks/dogfooding.md.
 *
 *   3. On Clerk's `useUser()` resolving to a signed-in user the
 *      component pulls a Clerk session JWT via `useAuth().getToken()`
 *      and calls `Spotlight.init({apiKey, jwt, userId, …})`.  The
 *      SDK trades pk + jwt for an `sst_*` session token via
 *      `/v1/sdk/elevate`; that session token is what backs every
 *      identity-gated call (Pinpoint posts, progress writes, etc.).
 *      Without the JWT the backend correctly refuses to trust the
 *      SDK-supplied `userId` and 422s help-request POSTs as
 *      `anonymous_request` — fix landed under card 112.  On
 *      sign-out we call `Spotlight.destroy()` so the widget
 *      doesn't leak across identities (operator A signs out →
 *      operator B signs in → widget must re-init under the new
 *      identity).
 *
 *   4. The component is mounted *inside* `<ClerkProvider>` in
 *      `main.jsx`, which means it never mounts on the SetupPage
 *      fallback (which renders when no Clerk key is configured).
 *      That's the gating mechanism for "don't show the widget on
 *      the login/setup page".
 *
 * Security:
 *   - `pk_live_*` is publishable by design — safe to bundle.
 *   - We never call `Spotlight.init()` before Clerk has loaded
 *     (`isLoaded` gate).  Otherwise a signed-out user could file
 *     an anonymous ticket and the backend would 422
 *     (`anonymous_request`) — but worse, the operator inbox
 *     would never get the ticket and the user would think they'd
 *     contacted support.
 *   - `Spotlight.destroy()` cleanup on sign-out — identity bleed
 *     across users is the bug to avoid.
 *
 * Tenant routing:
 *   The SDK's `apiKey` ties this instance to *one* tenant — the
 *   Spotlight platform tenant.  A dashboard user who belongs to
 *   their own customer tenant *and* uses this dashboard files
 *   help tickets against Spotlight, not their own tenant.  That
 *   is intentional for dogfood; per-customer routing is out of
 *   scope (see card 049).
 */

import { useEffect } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';

const SDK_POLL_INTERVAL_MS = 50;
const SDK_POLL_TIMEOUT_MS = 10_000;

// Dogfood config for dashboard.spotlight.help (card 049).  pk_live is
// publishable by design and dashboard.spotlight.help is on the
// project's Sites allow-list — leaked from any other origin it does
// nothing.  Env vars override so dev / preview / staging can point
// at a different tenant without editing source.
const DOGFOOD_PK_KEY =
  'pk_live_19a0ad1806e8437489d941c695e60269_vrDY6AOzA5xXCS5SYdUZew2sM4gsvw';
const DOGFOOD_API_BASE = 'https://api.spotlight.help';
const DOGFOOD_PROJECT = 'dashboard-production';

/**
 * Wait for the deferred SDK script to define `window.Spotlight`.
 * Resolves to the global or `null` if the script never loaded
 * (e.g. ad-blocker, network failure).  We do NOT throw on timeout
 * because a missing widget is a degraded-but-functional state for
 * the dashboard, not a fatal error.
 */
function waitForSpotlight() {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve(null);
      return;
    }
    if (window.Spotlight) {
      resolve(window.Spotlight);
      return;
    }
    const startedAt = Date.now();
    const id = window.setInterval(() => {
      if (window.Spotlight) {
        window.clearInterval(id);
        resolve(window.Spotlight);
        return;
      }
      if (Date.now() - startedAt > SDK_POLL_TIMEOUT_MS) {
        window.clearInterval(id);
        console.warn(
          '[SpotlightDogfood] window.Spotlight not available after ' +
            `${SDK_POLL_TIMEOUT_MS}ms — help widget will not load.`,
        );
        resolve(null);
      }
    }, SDK_POLL_INTERVAL_MS);
  });
}

/**
 * Mount once near the React root inside `<ClerkProvider>`.  Renders
 * nothing — the SDK injects its own DOM directly under <body>.
 */
export function SpotlightDogfood() {
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();

  useEffect(() => {
    // Wait for Clerk before we touch the SDK — otherwise we might
    // boot the widget under a stale or empty identity.
    if (!isLoaded) return;

    const apiKey = import.meta.env.VITE_SPOTLIGHT_PK_KEY || DOGFOOD_PK_KEY;
    const baseUrl = import.meta.env.VITE_SPOTLIGHT_API_BASE || DOGFOOD_API_BASE;
    const project = import.meta.env.VITE_SPOTLIGHT_PROJECT || DOGFOOD_PROJECT;
    if (!apiKey) {
      if (import.meta.env.DEV) {
        console.info(
          '[SpotlightDogfood] no apiKey resolved — dogfood widget disabled.',
        );
      }
      return;
    }

    // Signed-out → ensure no widget is hanging around from a
    // prior session.  Calling destroy() before init() is a no-op
    // on a fresh load (the SDK guards on `instance == null`).
    if (!user) {
      if (window.Spotlight) {
        try {
          window.Spotlight.destroy();
        } catch (err) {
          console.warn('[SpotlightDogfood] destroy() failed', err);
        }
      }
      return;
    }

    // Signed-in → boot.  Wrap in an async closure so we can await
    // the SDK script landing without blocking the effect cleanup.
    let cancelled = false;
    (async () => {
      const sdk = await waitForSpotlight();
      if (cancelled || !sdk) return;

      const userEmail = user.primaryEmailAddress?.emailAddress || '';
      const userName = user.fullName || '';

      // The help widget also reads `window.__SPOTLIGHT_HOST_IDENTITY`
      // for the operator-side context block — set both so the
      // Pinpoint inbox sees a fully-populated user card.  Safe-key
      // filter on the SDK side strips anything we shouldn't be
      // sending.
      window.__SPOTLIGHT_HOST_IDENTITY = {
        host_user_id: user.id,
        host_user_email: userEmail,
        host_user_name: userName,
      };

      // Card 112 — Pinpoint posts (and any other identity-gated SDK
      // call) require the backend to RESOLVE a real user_id from a
      // signed JWT, not just trust the SDK-supplied string.  Pull the
      // active Clerk session JWT and hand it to the SDK so its
      // elevation handshake (`POST /v1/sdk/elevate`) trades it for an
      // `sst_*` session token.  Without this the backend 422s
      // `anonymous_request` on every help-request POST even though
      // the user is signed in.
      //
      // By default we ask Clerk for the plain session JWT (no
      // template).  Set `VITE_SPOTLIGHT_JWT_TEMPLATE` to opt in to a
      // named Clerk JWT template — useful when the Spotlight tenant
      // config pins a specific `jwt_audience` that only that
      // template emits.  We don't try a template unless the env var
      // names one, because `getToken({template: 'missing'})`
      // produces a noisy 404 in the network panel for every signed-
      // in render.
      const jwtTemplate = import.meta.env.VITE_SPOTLIGHT_JWT_TEMPLATE || null;
      let jwt = null;
      try {
        jwt = jwtTemplate
          ? await getToken({ template: jwtTemplate })
          : await getToken();
      } catch {
        // getToken() can throw if the underlying Clerk session goes
        // stale between effect runs.  Swallow it — Pinpoint will
        // 422 instead of crashing, which is the right degraded
        // mode.  No console.warn here: the SDK's own elevate()
        // will log if it actually needed the JWT and didn't get
        // one.
      }
      if (cancelled) return;

      try {
        // autoStart: true lets the SDK pick the right page_load tour
        // from the URL match — calling start() with no args under
        // autoStart: false threw "Failed to start tour undefined"
        // because nothing told it WHICH tour to begin (card 089).
        await sdk.init({
          apiKey,
          baseUrl,
          project,
          userId: user.id,
          userEmail,
          jwt: jwt || undefined,
          autoStart: true,
        });
      } catch (err) {
        console.error('[SpotlightDogfood] init failed', err);
      }
    })();

    return () => {
      cancelled = true;
      // Identity changed (sign-out, or sign-in-as-different-user) →
      // tear down the widget so the next boot starts clean.
      if (window.Spotlight) {
        try {
          window.Spotlight.destroy();
        } catch (err) {
          console.warn('[SpotlightDogfood] destroy() cleanup failed', err);
        }
      }
      // Also clear the host-identity passthrough so a subsequent
      // boot doesn't briefly see the prior user's identity.
      try {
        delete window.__SPOTLIGHT_HOST_IDENTITY;
      } catch {
        window.__SPOTLIGHT_HOST_IDENTITY = undefined;
      }
    };
  }, [isLoaded, user?.id, getToken]);

  return null;
}
